import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { Avatar, Box, Button, Chip, LinearProgress, Radio, Stack, TextField, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { AudioApi } from '../api/AudioApi';
import { IVerbaScholaAttemptSoundGrade, IVerbaScholaSpeakingReviewAttempt, IVerbaScholaStudentProgress, VerbaScholaApi } from '../api/VerbaScholaApi';
import { VerbaScholaEmptyState } from '../component/VerbaScholaTeacherShell';
import { BLUE, BORDER, ERROR, ERROR_SOFT, ERROR_TEXT, GREEN, INTER, NAVY, POPPINS, primaryButtonSx, PROGRESS_TRACK, SLATE, SOFT_BLUE, SUCCESS_SOFT, SUCCESS_TEXT, SURFACE_MUTED, SURFACE_TINT, WARNING, WARNING_SOFT, WARNING_TEXT } from '../style/verbaScholaStyles';

type SoundGrade = 'good' | 'ok' | 'bad';
type GradeValue = SoundGrade | null;
type AlignmentGrade = 'speech' | 'extra' | 'silence';
type SoundGradeKey = 'initial' | 'final' | 'tone';
type SoundGradeItem = {
    id?: number;
    character: string;
    pinyinExpected: string;
    pinyinSpoken: string;
    initial: GradeValue;
    final: GradeValue;
    tone: GradeValue;
    alignment: AlignmentGrade;
};

const soundGradeColor: Record<SoundGrade, string> = { good: GREEN, ok: WARNING, bad: ERROR };
const soundGradeTextColor: Record<SoundGrade, string> = { good: SUCCESS_TEXT, ok: WARNING_TEXT, bad: ERROR_TEXT };
const soundGradeLabel: Record<SoundGrade, string> = { good: 'Good', ok: 'Close', bad: 'Needs practice' };
const soundGradeMark: Record<SoundGrade, string> = { good: '✓', ok: '~', bad: '!' };
const soundGradeOrder: SoundGrade[] = ['good', 'ok', 'bad'];

function SectionCard({ children, sx = {} }: { children: React.ReactNode; sx?: Record<string, unknown> }) {
    return <Box sx={{ backgroundColor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '20px', ...sx }}>{children}</Box>;
}

function percentLabel(value: number | null | undefined, zeroWhenNull = false): string {
    if (value === null || value === undefined) return zeroWhenNull ? '0%' : '-';
    return Number.isFinite(value) ? `${Math.round(value)}%` : '0%';
}

function displayName(progress: IVerbaScholaStudentProgress | IVerbaScholaSpeakingReviewAttempt): string {
    return progress.display_name ?? progress.email ?? 'Student name unavailable';
}

function initialsForLabel(label: string): string {
    if (label.includes('@')) return label.charAt(0).toUpperCase();
    const words = label.split(/\s+/).filter(Boolean);
    return words.map(word => word.charAt(0)).slice(0, 2).join('').toUpperCase() || 'S';
}

function formatSubmitted(value: string | null | undefined): string {
    if (!value) return 'recently';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function normalizeSoundGrade(value: string | null | undefined): GradeValue {
    return value === 'good' || value === 'ok' || value === 'bad' ? value : null;
}

function normalizeAlignment(value: string | null | undefined): AlignmentGrade {
    return value === 'extra' || value === 'silence' ? value : 'speech';
}

function fromApiSoundGrade(grade: IVerbaScholaAttemptSoundGrade): SoundGradeItem {
    return {
        id: grade.id,
        character: grade.transcript_char || '-',
        pinyinExpected: grade.pinyin_expected || '-',
        pinyinSpoken: grade.pinyin_spoken || '-',
        initial: normalizeSoundGrade(grade.initial_score_grade),
        final: normalizeSoundGrade(grade.final_score_grade),
        tone: normalizeSoundGrade(grade.tone_score_grade),
        alignment: normalizeAlignment(grade.alignment_error),
    };
}

function toApiSoundGrade(item: SoundGradeItem, index: number): IVerbaScholaAttemptSoundGrade {
    return {
        index,
        transcript_char: item.character === '-' ? null : item.character,
        pinyin_expected: item.pinyinExpected === '-' ? null : item.pinyinExpected,
        pinyin_spoken: item.pinyinSpoken === '-' ? null : item.pinyinSpoken,
        initial_score_grade: item.initial,
        final_score_grade: item.final,
        tone_score_grade: item.tone,
        alignment_error: item.alignment,
    };
}

function defaultSoundGradesForAttempt(attempt: IVerbaScholaSpeakingReviewAttempt | null): SoundGradeItem[] {
    if (!attempt) return [];
    const characters = Array.from(attempt.expected_response || attempt.recording_text || '').filter(character => character.trim().length > 0);
    const expectedPinyin = attempt.pinyin_expected ?? attempt.pinyin_list_forced ?? [];
    const spokenPinyin = attempt.pinyin_list_transcript ?? attempt.pinyin_list_spoken ?? [];
    const length = Math.max(characters.length, expectedPinyin.length, spokenPinyin.length, 1);
    return Array.from({ length }).map((_, index) => ({
        character: characters[index] ?? '-',
        pinyinExpected: expectedPinyin[index] ?? '-',
        pinyinSpoken: spokenPinyin[index] ?? '-',
        initial: null,
        final: null,
        tone: null,
        alignment: characters[index] ? 'speech' : 'extra',
    }));
}

function TinySoundGrade({ value, label, onChange }: { value: GradeValue; label: string; onChange: (grade: SoundGrade) => void }) {
    const clickTimerRef = React.useRef<number | null>(null);
    const longTimerRef = React.useRef<number | null>(null);
    const longFiredRef = React.useRef(false);

    const clearClick = () => {
        if (clickTimerRef.current !== null) window.clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
    };
    const clearLong = () => {
        if (longTimerRef.current !== null) window.clearTimeout(longTimerRef.current);
        longTimerRef.current = null;
    };

    return (
        <Box
            role="button"
            tabIndex={0}
            aria-label={`${label}: ${value ? soundGradeLabel[value] : 'Not graded'}. Use arrow keys to change the grade, or G, O, and B to choose a grade directly.`}
            onPointerDown={() => {
                longFiredRef.current = false;
                clearLong();
                longTimerRef.current = window.setTimeout(() => {
                    longFiredRef.current = true;
                    clearClick();
                    onChange('bad');
                }, 600);
            }}
            onPointerUp={clearLong}
            onPointerLeave={clearLong}
            onClick={() => {
                if (longFiredRef.current) return;
                clearClick();
                clickTimerRef.current = window.setTimeout(() => {
                    onChange('good');
                    clearClick();
                }, 220);
            }}
            onDoubleClick={() => {
                clearClick();
                onChange('ok');
            }}
            onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onChange('good');
                    return;
                }
                const directGrade = event.key.toLowerCase() === 'g' ? 'good' : event.key.toLowerCase() === 'o' ? 'ok' : event.key.toLowerCase() === 'b' ? 'bad' : null;
                if (directGrade) {
                    event.preventDefault();
                    onChange(directGrade);
                    return;
                }
                if (event.key === 'Home' || event.key === 'End') {
                    event.preventDefault();
                    onChange(event.key === 'Home' ? 'good' : 'bad');
                    return;
                }
                if (event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
                    const nextIndex = value === null ? (direction > 0 ? 0 : soundGradeOrder.length - 1) : (soundGradeOrder.indexOf(value) + direction + soundGradeOrder.length) % soundGradeOrder.length;
                    onChange(soundGradeOrder[nextIndex]);
                }
            }}
            sx={{ width: 40, height: 40, borderRadius: '10px', cursor: 'pointer', outlineOffset: 2, backgroundColor: value ? `${soundGradeColor[value]}33` : PROGRESS_TRACK, display: 'grid', placeItems: 'center' }}
        >
            <Typography aria-hidden="true" sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 17, lineHeight: 1, color: value ? soundGradeTextColor[value] : SLATE }}>
                {value ? soundGradeMark[value] : '–'}
            </Typography>
        </Box>
    );
}

export default function VerbaScholaTeacherLessonSpeakingReviewPage({
    attempts,
    saving,
    onSave,
    onAction,
}: {
    attempts: IVerbaScholaSpeakingReviewAttempt[];
    saving: boolean;
    onSave: (attemptId: number, feedback: string, soundGrades?: IVerbaScholaAttemptSoundGrade[]) => Promise<IVerbaScholaAttemptSoundGrade[]>;
    onAction: (message: string) => void;
}) {
    const location = useLocation();
    const requestedAttemptId = Number(new URLSearchParams(location.search).get('attempt'));
    const [selectedAttemptId, setSelectedAttemptId] = React.useState<number | null>(requestedAttemptId > 0 ? requestedAttemptId : attempts[0]?.attempt_id ?? null);
    const selectedAttempt = attempts.find(attempt => attempt.attempt_id === selectedAttemptId) ?? attempts[0] ?? null;
    const [feedback, setFeedback] = React.useState(selectedAttempt?.teacher_feedback ?? '');
    const [playingId, setPlayingId] = React.useState<number | null>(null);
    const [soundGradesByAttempt, setSoundGradesByAttempt] = React.useState<Record<number, SoundGradeItem[]>>({});
    const [editedGradesByAttempt, setEditedGradesByAttempt] = React.useState<Record<number, boolean>>({});
    const awaitingCount = attempts.filter(attempt => !attempt.feedback_sent_at).length;
    const soundGrades = selectedAttempt ? (soundGradesByAttempt[selectedAttempt.attempt_id] ?? defaultSoundGradesForAttempt(selectedAttempt)) : [];

    React.useEffect(() => {
        if (attempts.length === 0) {
            setSelectedAttemptId(null);
            return;
        }
        if (!selectedAttemptId || !attempts.some(attempt => attempt.attempt_id === selectedAttemptId)) {
            setSelectedAttemptId(attempts.find(attempt => attempt.attempt_id === requestedAttemptId)?.attempt_id ?? attempts[0].attempt_id);
        }
    }, [attempts, selectedAttemptId, requestedAttemptId]);

    React.useEffect(() => {
        setFeedback(selectedAttempt?.teacher_feedback ?? '');
    }, [selectedAttempt?.attempt_id, selectedAttempt?.teacher_feedback]);

    React.useEffect(() => {
        if (!selectedAttempt || soundGradesByAttempt[selectedAttempt.attempt_id]) return;
        let cancelled = false;
        VerbaScholaApi.getAttemptSoundGrades(selectedAttempt.attempt_id)
            .then(response => {
                if (cancelled) return;
                const rows = response.data.length > 0
                    ? response.data.map(fromApiSoundGrade)
                    : defaultSoundGradesForAttempt(selectedAttempt);
                setSoundGradesByAttempt(current => current[selectedAttempt.attempt_id] ? current : ({ ...current, [selectedAttempt.attempt_id]: rows }));
            })
            .catch(() => {
                if (!cancelled) {
                    setSoundGradesByAttempt(current => current[selectedAttempt.attempt_id] ? current : ({ ...current, [selectedAttempt.attempt_id]: defaultSoundGradesForAttempt(selectedAttempt) }));
                }
            });
        return () => {
            cancelled = true;
        };
    }, [selectedAttempt, soundGradesByAttempt]);

    const updateSoundGrade = (index: number, key: SoundGradeKey, value: SoundGrade) => {
        if (!selectedAttempt) return;
        setEditedGradesByAttempt(current => ({ ...current, [selectedAttempt.attempt_id]: true }));
        setSoundGradesByAttempt(current => {
            const attemptGrades = (current[selectedAttempt.attempt_id] ?? defaultSoundGradesForAttempt(selectedAttempt)).map(item => ({ ...item }));
            attemptGrades[index] = { ...attemptGrades[index], [key]: value, alignment: 'speech' };
            return { ...current, [selectedAttempt.attempt_id]: attemptGrades };
        });
    };

    const updateAlignment = (index: number, value: AlignmentGrade) => {
        if (!selectedAttempt) return;
        setEditedGradesByAttempt(current => ({ ...current, [selectedAttempt.attempt_id]: true }));
        setSoundGradesByAttempt(current => {
            const attemptGrades = (current[selectedAttempt.attempt_id] ?? defaultSoundGradesForAttempt(selectedAttempt)).map(item => ({ ...item }));
            attemptGrades[index] = value === 'silence'
                ? { ...attemptGrades[index], initial: 'bad', final: 'bad', tone: 'bad', alignment: value }
                : { ...attemptGrades[index], alignment: value };
            return { ...current, [selectedAttempt.attempt_id]: attemptGrades };
        });
    };

    const markNothingSpoken = () => {
        if (!selectedAttempt) return;
        setEditedGradesByAttempt(current => ({ ...current, [selectedAttempt.attempt_id]: true }));
        setSoundGradesByAttempt(current => {
            const attemptGrades = (current[selectedAttempt.attempt_id] ?? defaultSoundGradesForAttempt(selectedAttempt)).map(item => ({ ...item, initial: 'bad' as SoundGrade, final: 'bad' as SoundGrade, tone: 'bad' as SoundGrade, alignment: 'silence' as AlignmentGrade }));
            return { ...current, [selectedAttempt.attempt_id]: attemptGrades };
        });
    };

    const saveCurrentAttempt = () => {
        if (!selectedAttempt) return;
        onSave(selectedAttempt.attempt_id, feedback, editedGradesByAttempt[selectedAttempt.attempt_id] ? soundGrades.map(toApiSoundGrade) : undefined)
            .then(returnedGrades => {
                setSoundGradesByAttempt(current => ({ ...current, [selectedAttempt.attempt_id]: returnedGrades.length ? returnedGrades.map(fromApiSoundGrade) : defaultSoundGradesForAttempt(selectedAttempt) }));
                setEditedGradesByAttempt(current => ({ ...current, [selectedAttempt.attempt_id]: false }));
            })
            .catch(() => undefined);
    };

    const playRecording = () => {
        if (!selectedAttempt) return;
        setPlayingId(selectedAttempt.attempt_id);
        AudioApi.getSentenceScoreAudio(selectedAttempt.sentence_score_id)
            .then(response => {
                const url = URL.createObjectURL(response.data);
                const audio = new Audio(url);
                audio.onended = () => {
                    URL.revokeObjectURL(url);
                    setPlayingId(null);
                };
                audio.onerror = () => {
                    URL.revokeObjectURL(url);
                    setPlayingId(null);
                    onAction('Could not play that recording.');
                };
                audio.play().catch(() => {
                    URL.revokeObjectURL(url);
                    setPlayingId(null);
                    onAction('Could not play that recording.');
                });
            })
            .catch(() => {
                setPlayingId(null);
                onAction('Could not load that recording.');
            });
    };

    const openNext = () => {
        if (!selectedAttempt) return;
        const currentIndex = attempts.findIndex(attempt => attempt.attempt_id === selectedAttempt.attempt_id);
        const nextAttempt = attempts.slice(currentIndex + 1).find(attempt => !attempt.feedback_sent_at) ?? attempts.find(attempt => !attempt.feedback_sent_at);
        if (nextAttempt) setSelectedAttemptId(nextAttempt.attempt_id);
        else onAction('All speaking attempts in this lesson have been reviewed.');
    };

    if (attempts.length === 0 || selectedAttempt === null) {
        return (
            <SectionCard>
                <Box sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>Speaking review</Typography>
                </Box>
                <VerbaScholaEmptyState message="No speaking attempts have been submitted for this lesson." />
            </SectionCard>
        );
    }

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '320px minmax(0, 1fr)' }, gap: 2.5, alignItems: 'start' }}>
            <SectionCard sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 2.25, borderBottom: `1px solid ${BORDER}` }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>Review queue</Typography>
                        <Chip label={`${awaitingCount} awaiting`} size="small" sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, color: awaitingCount ? ERROR_TEXT : SUCCESS_TEXT, backgroundColor: awaitingCount ? ERROR_SOFT : SUCCESS_SOFT }} />
                    </Stack>
                </Box>
                {attempts.map((attempt, index) => {
                    const active = attempt.attempt_id === selectedAttempt.attempt_id;
                    const label = displayName(attempt);
                    return (
                        <Button
                            key={attempt.attempt_id}
                            fullWidth
                            onClick={() => setSelectedAttemptId(attempt.attempt_id)}
                            sx={{ justifyContent: 'flex-start', px: 2, py: 1.5, borderRadius: 0, borderTop: index === 0 ? 0 : `1px solid ${BORDER}`, textAlign: 'left', textTransform: 'none', backgroundColor: active ? SOFT_BLUE : '#fff', '&:hover': { backgroundColor: active ? SOFT_BLUE : SURFACE_TINT } }}
                        >
                            <Avatar sx={{ width: 34, height: 34, mr: 1.25, backgroundColor: active ? BLUE : NAVY, fontFamily: POPPINS, fontSize: 11 }}>{initialsForLabel(label)}</Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: NAVY }}>{label}</Typography>
                                <Typography noWrap sx={{ fontFamily: INTER, fontSize: 11, color: SLATE, mt: 0.15 }}>{attempt.exercise_title ?? attempt.step_title ?? 'Speaking'} - AI {percentLabel(attempt.score_mean, true)}</Typography>
                            </Box>
                            {attempt.feedback_sent_at && <CheckCircleRoundedIcon sx={{ ml: 1, color: GREEN, fontSize: 19 }} />}
                        </Button>
                    );
                })}
            </SectionCard>

            <Stack spacing={2.5}>
                <SectionCard sx={{ overflow: 'hidden' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
                        <Box>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>{displayName(selectedAttempt)}</Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>{selectedAttempt.exercise_title ?? selectedAttempt.step_title ?? 'Speaking'} - Submitted {formatSubmitted(selectedAttempt.submitted_at ?? selectedAttempt.completed_at)}</Typography>
                        </Box>
                        <Chip label={selectedAttempt.feedback_sent_at ? selectedAttempt.student_reviewed_feedback ? 'Reviewed by student' : 'Sent to student' : selectedAttempt.teacher_reviewed_at ? 'Draft' : 'Awaiting review'} size="small" sx={{ alignSelf: { sm: 'flex-start' }, fontFamily: INTER, fontWeight: 700, color: selectedAttempt.feedback_sent_at ? SUCCESS_TEXT : WARNING_TEXT, backgroundColor: selectedAttempt.feedback_sent_at ? SUCCESS_SOFT : WARNING_SOFT }} />
                    </Stack>

                    <Box sx={{ p: 2.5 }}>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: SLATE, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Prompt</Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 13.5, lineHeight: 1.55, color: NAVY, mt: 0.5 }}>{selectedAttempt.prompt_text || selectedAttempt.step_title || 'Speaking prompt'}</Typography>
                        <Box sx={{ mt: 1.5, p: 1.75, borderRadius: '14px', backgroundColor: SURFACE_MUTED }}>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: SLATE }}>Expected response</Typography>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY, mt: 0.35 }}>{selectedAttempt.expected_response || '-'}</Typography>
                        </Box>

                        <Button variant="contained" startIcon={<PlayArrowRoundedIcon />} onClick={playRecording} sx={{ ...primaryButtonSx, mt: 2, borderRadius: '11px', minWidth: 180 }}>
                            {playingId === selectedAttempt.attempt_id ? 'Playing recording...' : 'Play recording'}
                        </Button>
                        {playingId === selectedAttempt.attempt_id && <LinearProgress aria-label="Playing student recording" sx={{ mt: 1, maxWidth: 360, height: 5, borderRadius: 3, backgroundColor: PROGRESS_TRACK, '& .MuiLinearProgress-bar': { backgroundColor: BLUE } }} />}
                    </Box>
                </SectionCard>

                <SectionCard sx={{ p: 2.5, overflow: 'hidden' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>Individual sound grading</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>Automated score: {percentLabel(selectedAttempt.score_mean)}</Typography>
                            <Chip label="Teacher sound grades" size="small" sx={{ alignSelf: { sm: 'flex-start' }, fontFamily: INTER, fontWeight: 700, color: NAVY, backgroundColor: SOFT_BLUE }} />
                        </Stack>
                    </Stack>

                    <Box sx={{ overflowX: 'auto', mt: 2, pb: 1 }}>
                        <Box
                            data-testid="teacher-sound-grading-grid"
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: `118px repeat(${soundGrades.length}, 48px)`,
                                columnGap: 0.75,
                                rowGap: 0.75,
                                alignItems: 'center',
                                minWidth: 'max-content',
                            }}
                        >
                            <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>Target</Typography>
                            {soundGrades.map((item, index) => (
                                <Typography key={`pinyin-${index}`} align="center" sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, color: NAVY }}>{item.pinyinExpected}</Typography>
                            ))}

                            <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>Heard</Typography>
                            {soundGrades.map((item, index) => {
                                const matchesTarget = item.pinyinSpoken === item.pinyinExpected;
                                return (
                                    <Typography key={`spoken-${index}`} align="center" sx={{ px: 0.35, py: 0.4, borderRadius: '8px', fontFamily: INTER, fontSize: 11, fontWeight: matchesTarget ? 400 : 700, color: matchesTarget ? SLATE : ERROR_TEXT, backgroundColor: matchesTarget ? PROGRESS_TRACK : ERROR_SOFT }}>
                                        {item.pinyinSpoken}
                                    </Typography>
                                );
                            })}

                            <Box />
                            {soundGrades.map((item, index) => {
                                const combinedGrade: GradeValue = item.initial === 'bad' || item.final === 'bad' || item.tone === 'bad' ? 'bad' : item.initial === 'ok' || item.final === 'ok' || item.tone === 'ok' ? 'ok' : item.initial === 'good' || item.final === 'good' || item.tone === 'good' ? 'good' : null;
                                return (
                                    <Box key={`character-${index}`} sx={{ height: 38, display: 'grid', placeItems: 'center', borderRadius: '8px', backgroundColor: item.character === '-' || item.alignment === 'silence' || !combinedGrade ? PROGRESS_TRACK : `${soundGradeColor[combinedGrade]}26` }}>
                                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 21, color: NAVY }}>{item.character}</Typography>
                                    </Box>
                                );
                            })}

                            {(['initial', 'final', 'tone'] as SoundGradeKey[]).map(key => (
                                <React.Fragment key={key}>
                                    <Typography sx={{ fontFamily: INTER, fontSize: 12, color: NAVY, textTransform: 'capitalize' }}>{key}</Typography>
                                    {soundGrades.map((item, index) => (
                                        <Box key={`${key}-${index}`} sx={{ display: 'grid', placeItems: 'center' }}>
                                            {item.character === '-'
                                                ? <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: PROGRESS_TRACK }} />
                                                : <TinySoundGrade value={item[key]} label={`${item.character} ${key}`} onChange={grade => updateSoundGrade(index, key, grade)} />}
                                        </Box>
                                    ))}
                                </React.Fragment>
                            ))}

                            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: NAVY }}>Speech</Typography>
                            {soundGrades.map((item, index) => (
                                item.character === '-'
                                    ? <Box key={`speech-${index}`} sx={{ height: 30 }} />
                                    : <Radio key={`speech-${index}`} size="small" checked={item.alignment === 'speech'} onChange={() => updateAlignment(index, 'speech')} inputProps={{ 'aria-label': `${item.character}: speech` }} sx={{ p: 0.5, justifySelf: 'center', color: BLUE }} />
                            ))}

                            <Tooltip title="Student spoke an extra character" arrow placement="left">
                                <Stack direction="row" alignItems="center" spacing={0.4}>
                                    <Typography sx={{ fontFamily: INTER, fontSize: 12, color: NAVY }}>Extra speech</Typography>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: BLUE }} />
                                </Stack>
                            </Tooltip>
                            {soundGrades.map((item, index) => (
                                item.character === '-'
                                    ? <Radio key={`extra-${index}`} size="small" checked={item.alignment === 'extra'} onChange={() => updateAlignment(index, 'extra')} inputProps={{ 'aria-label': `Extra speech: ${item.pinyinSpoken}` }} sx={{ p: 0.5, justifySelf: 'center', color: BLUE }} />
                                    : <Box key={`extra-${index}`} sx={{ height: 30 }} />
                            ))}

                            <Tooltip title="Student did not speak this character" arrow placement="left">
                                <Stack direction="row" alignItems="center" spacing={0.4}>
                                    <Typography sx={{ fontFamily: INTER, fontSize: 12, color: NAVY }}>Silence / noise</Typography>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: BLUE }} />
                                </Stack>
                            </Tooltip>
                            {soundGrades.map((item, index) => (
                                <Radio key={`silence-${index}`} size="small" checked={item.alignment === 'silence'} onChange={() => updateAlignment(index, 'silence')} inputProps={{ 'aria-label': `${item.character}: silence or noise` }} sx={{ p: 0.5, justifySelf: 'center', color: BLUE }} />
                            ))}
                        </Box>
                    </Box>

                    <TextField label="Teacher feedback" value={feedback} onChange={event => setFeedback(event.target.value)} fullWidth multiline minRows={4} sx={{ mt: 2 }} />

                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mt: 1.25 }}>
                        <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE }}><strong>Pointer:</strong> click Good, double-click Close, long-press Needs practice.&nbsp; <strong>Keyboard:</strong> arrows cycle; G/O/B select directly.</Typography>
                        <Stack direction="row" spacing={1}>
                            <Button onClick={markNothingSpoken} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12, color: BLUE, textTransform: 'none' }}>No words were spoken</Button>
                            <Button variant="contained" onClick={saveCurrentAttempt} disabled={saving} sx={{ ...primaryButtonSx, borderRadius: '10px', px: 2 }}>{saving ? 'Sending...' : 'Save and send'}</Button>
                            <Button endIcon={<NavigateNextRoundedIcon />} onClick={openNext} sx={{ color: BLUE, fontFamily: INTER, fontWeight: 700 }}>Next</Button>
                        </Stack>
                    </Stack>
                </SectionCard>
            </Stack>
        </Box>
    );
}
